import { CircularProgress, Grid } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { Supplier } from '../../../../api/supplier/suppliers.types';
import { supplierGroupsAPI } from '../../../../api/supplierGroup/supplierGroupAPI';
import { supplierIntegrationDetailsResponse } from '../../../../api/supplierGroup/suppliergroup.types';
import { taxisAPI } from '../../../../api/vehicle/taxisAPI';
import IntegrationManager from './IntegrationManager';
import IntegrationSetup, { IntegrationWizardData } from './IntegrationSetup';
import VehicleMapping from './steps/VehicleMapping';
import { isSingletonSupplierGroup } from './supplierCredentialConfig';

const IntegrationPanel: FC = () => {
  const queryCache = useQueryClient()
  const [vehicleMappingKey, setVehicleMappingKey] = useState(0)
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )

  const supplierGroupDetailsCall = useQuery(
    ['getSupplierGroupsBySupplier', supplier?.supplierGroupId, supplier?.id],
    async () =>
      supplierGroupsAPI.getSupplierGroupsBySupplier(
        supplier.supplierGroupId!,
        supplier.id,
      ),
    { refetchOnWindowFocus: false, enabled: !!supplier?.supplierGroupId },
  )

  const vehiclesCall = useQuery(
    ['getTaxisBySupplierId', supplier?.id],
    async () => taxisAPI.getTaxisBySupplierId(supplier.id),
    { refetchOnWindowFocus: false, enabled: !!supplier?.supplierGroupId },
  )

  if (supplier === null) {
    return null
  }

  const handleCredentialsUpdated = () => {
    queryCache.invalidateQueries({
      queryKey: [
        'getSupplierGroupsBySupplier',
        supplier.supplierGroupId,
        supplier.id,
      ],
    })
    setVehicleMappingKey((k) => k + 1)
  }

  if (!supplier.supplierGroupId) {
    return (
      <Grid container padding={7} spacing={4} direction="column">
        <IntegrationSetup />
      </Grid>
    )
  }

  if (supplierGroupDetailsCall.isLoading || vehiclesCall.isLoading) {
    return <CircularProgress />
  }

  if (isSingletonSupplierGroup(supplier.supplierGroupId)) {
    return (
      <Grid container spacing={4} paddingInlineEnd={5}>
        <Grid item xs={12}>
          <IntegrationManager
            supplierGroupDetails={supplierGroupDetailsCall.data?.data}
            onCredentialsUpdated={handleCredentialsUpdated}
          />
        </Grid>
      </Grid>
    )
  }

  const vehicles = vehiclesCall.data?.data.value ?? []
  const hasVehicles = vehicles.length > 0
  const allMapped =
    hasVehicles &&
    vehicles.every(
      (v) => v.supplierReference !== undefined && v.supplierReference !== null,
    )

  const details = supplierGroupDetailsCall.data?.data as
    | (supplierIntegrationDetailsResponse & { endpoint?: string })
    | undefined
  const existingCredentials: IntegrationWizardData = {
    supplierGroupId: supplier.supplierGroupId,
    endpoint: details?.endpoint,
    apiKey: details?.apiKey,
    appKey: details?.appKey,
    appSecret: details?.appSecret,
    accountNumber: details?.accountNumber,
    unmanagedAccountNumber: details?.unmanagedAccountNumber,
    dispatchReference: details?.dispatchReference,
    isExistingConfig: true,
  }

  if (!hasVehicles) {
    return (
      <Grid container padding={7} spacing={4} direction="column">
        <IntegrationSetup
          initialStep={2}
          initialWizardData={existingCredentials}
        />
      </Grid>
    )
  }

  if (!allMapped) {
    return (
      <Grid container padding={7} spacing={4} direction="column">
        <IntegrationSetup
          initialStep={3}
          initialWizardData={existingCredentials}
        />
      </Grid>
    )
  }
  return (
    <Grid container spacing={4} paddingInlineEnd={5}>
      <Grid item xs={12} md={5}>
        <IntegrationManager
          supplierGroupDetails={supplierGroupDetailsCall.data?.data}
          onCredentialsUpdated={handleCredentialsUpdated}
        />
      </Grid>
      <Grid item xs={12} md={7}>
        <VehicleMapping key={vehicleMappingKey} supplier={supplier} />
      </Grid>
    </Grid>
  )
}

export default IntegrationPanel
